import {
  differenceInCalendarDays,
  eachMonthOfInterval,
  endOfMonth,
  format,
  isSameMonth,
  isValid,
  lastDayOfMonth,
  max,
  min,
  startOfMonth,
} from 'date-fns'

import {
  ACCOUNT_TYPES,
  RECONCILIATION_STATUSES,
} from '@/modules/projects/constants'
import {
  isChecksCycle,
  isEmptyChecksCycle,
} from '@/modules/projects/status/statement_segment'
import type {
  AccountTypes,
  ReconciliationAccount,
  Statement,
  StatementSegment,
  Transaction,
} from '@/modules/projects/stores/types'
import { parseCalendarDate } from '@/utils'
import type {
  AccountCoverage,
  CoverageStatus,
  GapSide,
  MonthlyStatus,
  StatementCoverage,
} from './types'

// When two cycles cover the same month for one account, the higher-priority status wins so a
// problem is never hidden. 'member_activity' loses to every verdict: if a last4 is ever a real
// reconciled account somewhere, that reading wins. 'missing' is the absence of coverage, never merged.
const STATUS_PRIORITY: CoverageStatus[] = [
  'needs_review',
  'unverifiable',
  'reconciled',
  'not_checked',
  'member_activity',
]

const EMPTY_STATUS_COUNTS: Record<CoverageStatus, number> = {
  reconciled: 0,
  needs_review: 0,
  unverifiable: 0,
  not_checked: 0,
  member_activity: 0,
  verified_gap: 0,
  missing: 0,
}

// Two cycle balances chain when they agree to the cent (floats, so a hair of slack).
const CHAIN_TOLERANCE = 0.015

// A real statement cycle (monthly or a calendar quarter) touches at most this many months. A segment
// spanning more than this was stretched by a bad date, so we distrust its span and paint only the
// months that actually have activity, killing phantom "reconciled" months.
const PLAUSIBLE_CYCLE_MONTHS = 4

export const worseStatus = (
  current: CoverageStatus,
  candidate: CoverageStatus,
): CoverageStatus =>
  STATUS_PRIORITY.indexOf(current) <= STATUS_PRIORITY.indexOf(candidate)
    ? current
    : candidate

const monthKey = (date: Date) => format(date, 'yyyy-MM')

const sortKeyFor = (account: AccountCoverage): string =>
  account.parentId ? `${account.parentId}~${account.id}` : account.id

// The engine stores a cycle's account type as a raw enum key (e.g. 'credit_card'), while transactions
// reach us already humanized by lukita ('Credit card'). Mirror that so a zero-transaction account's
// badge matches the others. Returns undefined for anything outside the known vocabulary.
const normalizeAccountType = (raw: string): AccountTypes | undefined => {
  const words = raw.trim().replace(/_/g, ' ').toLowerCase()
  if (!words) return undefined
  const humanized = words.charAt(0).toUpperCase() + words.slice(1)
  return ACCOUNT_TYPES.includes(humanized as AccountTypes)
    ? (humanized as AccountTypes)
    : undefined
}

// The accounts a cycle touches: those with a reconcile row plus any it couldn't verify. Folded card
// members get their own member_activity rows, derived separately in segmentCoverageEntries.
const segmentAccountLast4s = (segment: StatementSegment): string[] => {
  const detail = segment.reconciliationDetail
  if (!detail) return []

  const last4s = new Set<string>()
  detail.accounts?.forEach((account) => last4s.add(account.last4))
  detail.unverifiedLast4S?.forEach((last4) => last4s.add(last4))
  return Array.from(last4s)
}

// Every last4 that is a real balance-carrying (or brokerage) account anywhere in the project. A
// member row is only born for a last4 outside this set, so a card can never shadow a real account.
const collectPrimaryLast4s = (statements: Statement[]): Set<string> => {
  const primaries = new Set<string>()
  statements.forEach((statement) => {
    statement.segments?.forEach((segment) => {
      segmentAccountLast4s(segment).forEach((last4) => primaries.add(last4))
      segment.securitiesDetail?.accounts?.forEach((account) =>
        primaries.add(account.accountLast4),
      )
    })
  })
  return primaries
}

// Which accounts a cycle paints. The reconcile detail is the authority whenever it names an account:
// it is what separates a balance-carrying account from a member card. A cycle nothing checked has
// none (no feature, or never run), so its own rows name the accounts instead, the grid still knows
// which cycle painted each cell, and the cell reads "not checked".
export const paintedAccountLast4s = (segment: StatementSegment): string[] => {
  const checked = segmentAccountLast4s(segment)
  if (checked.length > 0) return checked

  const last4s = (segment.accountNumbers ?? [])
    .map((account) => account.last4)
    .filter(Boolean)
  return Array.from(new Set(last4s))
}

export const accountStatusInSegment = (
  segment: StatementSegment,
  last4: string,
): CoverageStatus => {
  // Nothing checked this cycle's balances, so it has no verdict to report, not even "unverifiable".
  // A skipped cycle is the same: the engine chose not to read it. So is a checks cycle with no rows.
  const detail = segment.reconciliationDetail
  if (
    !detail ||
    segment.reconciliationStatus === RECONCILIATION_STATUSES.skipped ||
    isEmptyChecksCycle(segment)
  )
    return 'not_checked'
  if (!segmentAccountLast4s(segment).includes(last4)) return 'not_checked'
  if (isAcceptedByHand(segment)) return 'reconciled'
  if (isDoubtOnNoAccount(segment)) return 'needs_review'

  const account = worstAccountOf(detail.accounts ?? [], last4)
  if (account?.reconciled) return 'reconciled'

  const isUnverified =
    detail.unverifiedLast4S?.includes(last4) ||
    account?.begin == null ||
    account?.end == null
  if (isUnverified) return 'unverifiable'

  return 'needs_review'
}

// A checking and its credit line can print under one number. The one that misses decides the cell,
// so a balanced checking never hides its credit line's gap.
const worstAccountOf = (accounts: ReconciliationAccount[], last4: string) => {
  const sharing = accounts.filter((entry) => entry.last4 === last4)
  return sharing.find((entry) => !entry.reconciled) ?? sharing[0]
}

// A cycle can need review for a reason no account carries (a doubtful cut, more rows than its register
// A cycle accepted by hand is reconciled for every account it paints, whatever each account read.
const isAcceptedByHand = (segment: StatementSegment) =>
  segment.manuallyReconciled === true &&
  segment.reconciliationStatus === RECONCILIATION_STATUSES.reconciled

// prints). Then every account it paints shows that doubt, so no cell reads better than the cycle.
const isDoubtOnNoAccount = (segment: StatementSegment) =>
  segment.reconciliationStatus === RECONCILIATION_STATUSES.needsReview &&
  !segment.reconciliationDetail?.accounts?.some(
    (account) => !account.reconciled && account.gap != null,
  )

// A checks cycle prints no account period: its dates are only its first and last item, often years
// apart. So it never paints a month or anchors the balance chain, even when it kept a few rows.
// The Reconciliation tab still lists it, so pages that need review never vanish.
const paintingSegments = (statement: Statement): StatementSegment[] =>
  (statement.segments ?? []).filter((segment) => !isChecksCycle(segment))

const holdsOnlyChecksCycles = (statement: Statement): boolean => {
  const segments = statement.segments ?? []
  return segments.length > 0 && segments.every(isChecksCycle)
}

interface PeriodWindow {
  start: Date
  end: Date
}

// A printed period plus the cycle that printed it, so a cell can point back at its own segment.
interface CycleWindow extends PeriodWindow {
  segmentId: number
}

// A dated cycle for one account. The balances are null when the bank's printed figure was
// unreadable: such a cycle can still be named beside a gap, but it cannot anchor the chain.
interface DatedCycle {
  segmentId: number
  start: Date
  end: Date
  beginBalance: number | null
  endBalance: number | null
}

interface BalanceCycle extends DatedCycle {
  beginBalance: number
  endBalance: number
}

const hasBalances = (cycle: DatedCycle): cycle is BalanceCycle =>
  cycle.beginBalance != null && cycle.endBalance != null

// Every dated cycle per account, sorted by close date. The chain verdict below needs the printed
// balances, not the reconcile verdict: even an unreconciled cycle anchors the chain with what the
// bank printed on the page.
const collectDatedCycles = (
  statements: Statement[],
): Map<string, DatedCycle[]> => {
  const cycles = new Map<string, DatedCycle[]>()
  statements.forEach((statement) => {
    paintingSegments(statement).forEach((segment) => {
      const start = segment.startDate
        ? parseCalendarDate(segment.startDate)
        : null
      const end = segment.endDate ? parseCalendarDate(segment.endDate) : null
      if (!start || !end || !isValid(start) || !isValid(end) || end < start) {
        return
      }
      segment.reconciliationDetail?.accounts?.forEach((account) => {
        const list = cycles.get(account.last4) ?? []
        list.push({
          segmentId: segment.id,
          start,
          end,
          beginBalance: account.begin ?? null,
          endBalance: account.end ?? null,
        })
        cycles.set(account.last4, list)
      })
    })
  })
  cycles.forEach((list) =>
    list.sort((first, second) => first.end.getTime() - second.end.getTime()),
  )
  return cycles
}

const closingSide = (cycle: DatedCycle): GapSide => ({
  segmentId: cycle.segmentId,
  periodStart: cycle.start,
  periodEnd: cycle.end,
  balance: cycle.endBalance,
})

const openingSide = (cycle: DatedCycle): GapSide => ({
  segmentId: cycle.segmentId,
  periodStart: cycle.start,
  periodEnd: cycle.end,
  balance: cycle.beginBalance,
})

type GapVerdict = Pick<
  MonthlyStatus,
  'status' | 'gapAmount' | 'gapBefore' | 'gapAfter'
>

// With no balanced pair around the month, the nearest statements on each side are still named,
// so the user sees which one has no balance to compare.
const nearestNeighbors = (
  cycles: DatedCycle[],
  monthStart: Date,
): GapVerdict => {
  const before = cycles.filter((cycle) => cycle.start < monthStart).at(-1)
  const after = cycles
    .filter((cycle) => cycle.start >= monthStart)
    .sort((first, second) => first.start.getTime() - second.start.getTime())[0]
  return {
    status: 'missing',
    gapBefore: before && closingSide(before),
    gapAfter: after && openingSide(after),
  }
}

// The verdict for a month no statement spans. If the statements on either side of the hole chain
// (the next cycle opens with exactly what the prior one closed at), no money moved off the record:
// the bank issued no statement for the span and nothing is missing. If the balances jump, one or
// more statements ARE missing, and the jump is the amount that moved undisclosed.
const chainVerdictForMonth = (
  datedCycles: DatedCycle[] | undefined,
  monthStart: Date,
): GapVerdict => {
  if (!datedCycles) return { status: 'missing' }
  const cycles = datedCycles.filter(hasBalances)
  const monthEnd = endOfMonth(monthStart)
  for (let index = 0; index < cycles.length - 1; index += 1) {
    const prior = cycles[index]
    const next = cycles[index + 1]
    const monthSitsInGap =
      prior.end < next.start &&
      prior.end <= monthEnd &&
      next.start >= monthStart
    if (!monthSitsInGap) continue
    const jump = next.beginBalance - prior.endBalance
    const sides = { gapBefore: closingSide(prior), gapAfter: openingSide(next) }
    if (Math.abs(jump) <= CHAIN_TOLERANCE) {
      return { status: 'verified_gap', ...sides }
    }
    return { status: 'missing', gapAmount: jump, ...sides }
  }
  return nearestNeighbors(datedCycles, monthStart)
}

const daysCoveredInMonth = (window: PeriodWindow, monthStart: Date): number => {
  const from = max([window.start, monthStart])
  const to = min([window.end, endOfMonth(monthStart)])
  return to < from ? 0 : differenceInCalendarDays(to, from) + 1
}

// The cycle that owns a calendar month: the one that closes in it (a bank names a statement by its
// closing date, e.g. an Aug 31 - Sep 30 cycle is the "September" statement), ties broken by whichever
// covers more of the month. A neighbor that only bleeds a few days in never beats the cycle that
// closes here, so clicking a month scopes to that month's own statement, not the union of every
// cycle touching it.
const ownsMonthBetter = (
  candidate: PeriodWindow,
  incumbent: PeriodWindow,
  monthStart: Date,
): boolean => {
  const candidateClosesHere = isSameMonth(candidate.end, monthStart)
  const incumbentClosesHere = isSameMonth(incumbent.end, monthStart)
  if (candidateClosesHere !== incumbentClosesHere) return candidateClosesHere
  return (
    daysCoveredInMonth(candidate, monthStart) >
    daysCoveredInMonth(incumbent, monthStart)
  )
}

// The cycle a cell points back at, with the verdict that cycle gave the cell.
interface CellCycle {
  window: CycleWindow
  status: CoverageStatus
}

// Which of two cycles a shared cell belongs to. The worse verdict wins, so an amber cell always
// opens the cycle that needs review, never a same-month sibling that reconciled. Equal verdicts
// fall back to month ownership.
const claimsCell = (
  candidate: CellCycle,
  incumbent: CellCycle,
  monthStart: Date,
): boolean =>
  candidate.status === incumbent.status
    ? ownsMonthBetter(candidate.window, incumbent.window, monthStart)
    : worseStatus(incumbent.status, candidate.status) === candidate.status

interface SegmentEntry {
  last4: string
  month: Date
  status: CoverageStatus
  // The cycle's real printed period, so a click scopes the ledger to the whole period, not just
  // the single calendar month this cell sits in. Absent when the printed period was unusable.
  window?: CycleWindow
  // The primary account this member card folds into. Only set on member_activity entries.
  parentLast4?: string
}

// The months a cycle owns: the one it closes in plus every month it fully contains. A month the
// cycle only bleeds into belongs to the neighbor statement that closes there; painting it here
// would keep it green when that neighbor is missing, hiding the gap this chart exists to show.
// Closing, not opening: a bank that closes on the last business day opens the next cycle a day or
// two early, so an Aug 31 - Sep 30 cycle opens in August but is the September statement.
const ownedMonths = (months: Date[], window: PeriodWindow): Date[] =>
  months.filter(
    (month) =>
      isSameMonth(window.end, month) ||
      (window.start <= month && lastDayOfMonth(month) <= window.end),
  )

// The subset of `months` an account actually transacted in, within a cycle's window. Used only when
// a segment's period is too long to trust, so a stretched period can't paint an empty "reconciled" month.
const monthsWithActivity = (
  months: Date[],
  transactions: Transaction[],
  last4: string,
  window: PeriodWindow,
): Date[] => {
  const active = new Set<string>()
  transactions.forEach((transaction) => {
    if (transaction.accountNumber !== last4) return
    const date = parseCalendarDate(transaction.date)
    if (!isValid(date) || date < window.start || date > window.end) return
    active.add(monthKey(startOfMonth(date)))
  })
  return months.filter((month) => active.has(monthKey(month)))
}

// A cycle whose printed period is unusable (missing or backwards dates) still carries real
// reconcile verdicts. Paint each account's active months from the transaction rows so the verdict
// shows, instead of letting the whole statement fall back to "not checked".
const windowlessEntries = (
  segment: StatementSegment,
  transactions: Transaction[],
): SegmentEntry[] => {
  const entries: SegmentEntry[] = []
  paintedAccountLast4s(segment).forEach((last4) => {
    const status = accountStatusInSegment(segment, last4)
    const monthsSeen = new Set<string>()
    transactions.forEach((transaction) => {
      if (transaction.accountNumber !== last4) return
      const date = parseCalendarDate(transaction.date)
      if (!isValid(date)) return
      const month = startOfMonth(date)
      const key = monthKey(month)
      if (monthsSeen.has(key)) return
      monthsSeen.add(key)
      entries.push({ last4, month, status })
    })
  })
  return entries
}

// Every (account, month, status) a statement's cycles imply from their printed periods. A believable
// cycle paints the months it owns (so a dormant $0 reconciled account still shows, but a month it
// only bleeds into is left for that month's own statement). An implausibly long period paints only
// the months with real activity. Empty when the statement has no datable cycles or no reconcile
// detail (then the caller falls back to txn dates).
const segmentCoverageEntries = (
  segments: StatementSegment[],
  transactions: Transaction[],
  primaryLast4s: Set<string>,
): SegmentEntry[] => {
  const entries: SegmentEntry[] = []
  const rowLast4s = new Set(
    transactions.map((transaction) => transaction.accountNumber),
  )

  segments.forEach((segment) => {
    const start = segment.startDate
      ? parseCalendarDate(segment.startDate)
      : null
    const end = segment.endDate ? parseCalendarDate(segment.endDate) : null
    if (!start || !end || !isValid(start) || !isValid(end) || end < start) {
      entries.push(...windowlessEntries(segment, transactions))
      return
    }

    const window: CycleWindow = { start, end, segmentId: segment.id }
    const months = eachMonthOfInterval({
      start: startOfMonth(start),
      end: endOfMonth(end),
    })
    const periodIsPlausible = months.length <= PLAUSIBLE_CYCLE_MONTHS
    const owned = ownedMonths(months, window)

    // Cash accounts: those with a reconcile row plus any the cycle couldn't verify.
    paintedAccountLast4s(segment).forEach((last4) => {
      const status = accountStatusInSegment(segment, last4)
      const paintedMonths = periodIsPlausible
        ? owned
        : monthsWithActivity(months, transactions, last4, window)
      paintedMonths.forEach((month) =>
        entries.push({ last4, month, status, window }),
      )
    })

    // Member cards: each earns its own row so every spender is visible, but the statement only
    // prints balances for the primary, so the cell says "covered", never a reconcile verdict.
    // Only cards with real transaction rows qualify: the engine's member roster comes from its
    // plan and has carried invented last4s, while rows are ground truth.
    segment.reconciliationDetail?.accounts?.forEach((account) => {
      account.memberLast4S?.forEach((memberLast4) => {
        if (!rowLast4s.has(memberLast4) || primaryLast4s.has(memberLast4)) {
          return
        }
        const paintedMonths = periodIsPlausible
          ? owned
          : monthsWithActivity(months, transactions, memberLast4, window)
        paintedMonths.forEach((month) =>
          entries.push({
            last4: memberLast4,
            month,
            status: 'member_activity',
            window,
            parentLast4: account.last4,
          }),
        )
      })
    })

    // Brokerage accounts: a securities statement covers its owned months like a bank statement, and
    // per-account `validated` stands in for "reconciled". Securities have no transaction rows to fall
    // back on, so a stretched period paints only its end month (the as-of month) rather than nothing.
    segment.securitiesDetail?.accounts?.forEach((account) => {
      const status: CoverageStatus = account.validated
        ? 'reconciled'
        : 'needs_review'
      const paintedMonths = periodIsPlausible ? owned : [startOfMonth(end)]
      paintedMonths.forEach((month) =>
        entries.push({ last4: account.accountLast4, month, status, window }),
      )
    })
  })

  return entries
}

export const deriveStatementCoverage = (
  statements: Statement[],
  transactions: Transaction[],
): StatementCoverage | null => {
  // last4 -> (yyyy-MM -> best-known status)
  const coverage = new Map<string, Map<string, CoverageStatus>>()
  // last4 -> (yyyy-MM -> the cycle painting that cell), so a click filters by the real period and
  // opens that cycle. When two cycles touch the same month, one of them claims the cell (see
  // claimsCell), so the click never lands on a two-cycle union.
  const cellCycles = new Map<string, Map<string, CellCycle>>()
  const accountTypes = new Map<string, AccountTypes | undefined>()
  const transactionsByStatement = new Map<number, Transaction[]>()
  const coveredMonths: Date[] = []
  const memberParents = new Map<string, string>()
  const primaryLast4s = collectPrimaryLast4s(statements)
  const datedCycles = collectDatedCycles(statements)

  transactions.forEach((transaction) => {
    if (!transaction.accountNumber) return
    // Only a typed transaction claims the badge: an untyped one seen first must not block a later
    // typed read (or the reconcile-detail fill-in below) from setting the real type.
    if (
      transaction.accountType &&
      !accountTypes.get(transaction.accountNumber)
    ) {
      accountTypes.set(transaction.accountNumber, transaction.accountType)
    }
    const list = transactionsByStatement.get(transaction.statementId) ?? []
    list.push(transaction)
    transactionsByStatement.set(transaction.statementId, list)
  })

  // Zero-transaction accounts don't show up above, but the engine still printed their type in the
  // reconcile detail. Fill those in so a dormant account shows its type, not a blank badge.
  // Transactions win, so this only fills the gaps.
  statements.forEach((statement) => {
    statement.segments?.forEach((segment) => {
      segment.reconciliationDetail?.accounts?.forEach((account) => {
        if (!account.accountType || accountTypes.get(account.last4)) return
        accountTypes.set(
          account.last4,
          normalizeAccountType(account.accountType),
        )
      })
      segment.securitiesDetail?.accounts?.forEach((account) => {
        if (!account.accountType || accountTypes.get(account.accountLast4)) {
          return
        }
        accountTypes.set(
          account.accountLast4,
          normalizeAccountType(account.accountType),
        )
      })
    })
  })

  const record = (
    last4: string,
    month: Date,
    status: CoverageStatus,
    window?: CycleWindow,
  ) => {
    if (!isValid(month)) return
    coveredMonths.push(month)

    let months = coverage.get(last4)
    if (!months) {
      months = new Map()
      coverage.set(last4, months)
    }
    const key = monthKey(month)
    const existing = months.get(key)
    months.set(key, existing ? worseStatus(existing, status) : status)

    if (window) recordCellCycle(last4, key, { window, status }, month)
  }

  const recordCellCycle = (
    last4: string,
    key: string,
    cycle: CellCycle,
    month: Date,
  ) => {
    let cycles = cellCycles.get(last4)
    if (!cycles) {
      cycles = new Map()
      cellCycles.set(last4, cycles)
    }
    const existing = cycles.get(key)
    if (!existing || claimsCell(cycle, existing, month)) {
      cycles.set(key, cycle)
    }
  }

  statements.forEach((statement) => {
    const statementTransactions =
      transactionsByStatement.get(statement.id) ?? []
    const entries = segmentCoverageEntries(
      paintingSegments(statement),
      statementTransactions,
      primaryLast4s,
    )
    if (entries.length > 0) {
      entries.forEach(({ last4, month, status, window, parentLast4 }) => {
        if (parentLast4 && !memberParents.has(last4)) {
          memberParents.set(last4, parentLast4)
        }
        record(last4, month, status, window)
      })
      return
    }
    if (holdsOnlyChecksCycles(statement)) return

    // Legacy or unprocessed: no usable cycles, so fall back to the months its transactions land in,
    // marked "not checked" since we never reconciled them.
    const fallbackTransactions = transactionsByStatement.get(statement.id) ?? []
    fallbackTransactions.forEach((transaction) => {
      const date = parseCalendarDate(transaction.date)
      if (!isValid(date)) return
      record(transaction.accountNumber, startOfMonth(date), 'not_checked')
    })
  })

  if (coverage.size === 0 || coveredMonths.length === 0) return null

  const globalMin = startOfMonth(min(coveredMonths))
  const globalMax = endOfMonth(max(coveredMonths))

  const accounts: AccountCoverage[] = Array.from(coverage.entries())
    .map(([last4, months]): AccountCoverage => {
      const monthDates = Array.from(months.keys()).map((key) =>
        startOfMonth(parseCalendarDate(`${key}-01`)),
      )
      const accStart = startOfMonth(min(monthDates))
      const accEnd = endOfMonth(max(monthDates))

      const statusCounts: Record<CoverageStatus, number> = {
        ...EMPTY_STATUS_COUNTS,
      }
      const cyclesByMonth = cellCycles.get(last4)
      const cycles = datedCycles.get(last4)
      const monthlyStatus: MonthlyStatus[] = eachMonthOfInterval({
        start: accStart,
        end: accEnd,
      }).map((monthDate) => {
        const key = monthKey(monthDate)
        const covered = months.get(key)
        const verdict: GapVerdict = covered
          ? { status: covered }
          : chainVerdictForMonth(cycles, monthDate)
        statusCounts[verdict.status] += 1
        const window = cyclesByMonth?.get(key)?.window
        return {
          date: monthDate,
          status: verdict.status,
          periodStart: window?.start,
          periodEnd: window?.end,
          segmentId: window?.segmentId,
          gapAmount: verdict.gapAmount,
          gapBefore: verdict.gapBefore,
          gapAfter: verdict.gapAfter,
        }
      })

      const missingMonths = monthlyStatus.filter(
        (entry) => entry.status === 'missing',
      )
      const missingText =
        missingMonths.length === 0
          ? 'Complete'
          : missingMonths
              .map((entry) => format(entry.date, "MMM ''yy"))
              .join(', ')

      return {
        id: last4,
        parentId: memberParents.get(last4),
        type: accountTypes.get(last4),
        minDate: accStart,
        maxDate: accEnd,
        monthlyStatus,
        rangeText: `${format(accStart, 'MMM yyyy')} - ${format(accEnd, 'MMM yyyy')}`,
        missingText,
        statusCounts,
      }
    })
    // Members sort directly under their primary: the tilde suffixes a member's key onto its
    // parent's, so "6009" < "6009~2040" < "8021" keeps each family together.
    .sort((first, second) =>
      sortKeyFor(first).localeCompare(sortKeyFor(second)),
    )

  return { globalMin, globalMax, accounts }
}
